import { CircleNotch, Image as ImageIcon, Minus, Plus, UploadSimple } from "@phosphor-icons/react";
import { livePhotoUrl } from "@/lib/fmUtils";

export function DrawerImagesSection({
  containerFields,
  layout,
  record,
  photoVersion,
  localVersions,
  imgErrorMap,
  onImgError,
  canEdit,
  canUpload,
  isCreate,
  uploadingField,
  zoomingField,
  onTriggerUpload,
  onPhotoZoom,
  fileInputRef,
  onFileSelected,
  showZoomControl = true,
}) {
  if (containerFields.length === 0) return null;

  return (
    <div>
      <p className="mb-2 text-xs uppercase tracking-[0.05em] text-zinc-500">Images</p>
      <div className="grid grid-cols-1 gap-2">
        {containerFields.map((f) => {
          const hasError = imgErrorMap[f.name];
          const src =
            record?.recordId && !hasError
              ? livePhotoUrl(layout, record.recordId, f.name, photoVersion || localVersions[f.name])
              : null;
          const isUploading = uploadingField === f.name;
          return (
            <div
              key={f.name}
              className="group relative flex aspect-square flex-col items-center justify-center gap-1 overflow-hidden rounded-sm border border-zinc-300 bg-zinc-50"
              data-testid={`product-image-${f.name}`}
            >
              {src ? (
                <img
                  src={src}
                  alt={f.name}
                  className="h-full w-full object-contain"
                  onError={() => onImgError(f.name)}
                />
              ) : (
                <>
                  <ImageIcon size={20} className="text-zinc-400" />
                  <span className="px-1 text-center text-[10px] text-zinc-400">{f.name}</span>
                </>
              )}
              {canUpload && !isCreate && f.name === "photo" && (
                <button
                  type="button"
                  onClick={() => onTriggerUpload(f.name)}
                  disabled={isUploading}
                  className="absolute bottom-1 right-1 flex items-center gap-1 rounded-sm border border-zinc-300 bg-white/90 px-2 py-1.5 shadow-sm transition-colors duration-150 hover:bg-zinc-100 disabled:opacity-70"
                  title="Replace image (JPEG/PNG)"
                  data-testid={`product-image-upload-button-${f.name}`}
                >
                  {isUploading ? (
                    <CircleNotch size={14} className="animate-spin text-blue-600" />
                  ) : (
                    <UploadSimple size={14} className="text-zinc-600" />
                  )}
                  <span className="text-[10px] font-medium text-zinc-600">{isUploading ? "Uploading..." : "Replace"}</span>
                </button>
              )}
            </div>
          );
        })}
      </div>
      {showZoomControl && canEdit && !isCreate && layout === "main products" && containerFields.some((f) => f.name === "photo") && (
        <div
          className="mt-2 flex items-center gap-2 rounded-sm border border-zinc-300 bg-zinc-100 px-2 py-1.5"
          data-testid="product-photo-size-control"
        >
          <span className="text-xs font-medium text-zinc-600">Product Size in Photo:</span>
          <button
            type="button"
            onClick={() => onPhotoZoom("photo", "shrink")}
            disabled={zoomingField === "photo"}
            className="flex h-7 w-7 items-center justify-center rounded-sm border border-zinc-400 bg-white shadow-sm hover:bg-zinc-50 disabled:opacity-60"
            title="Shrink product (add more background margin)"
            data-testid="product-image-zoom-out-button-photo"
          >
            {zoomingField === "photo" ? (
              <CircleNotch size={14} className="animate-spin text-blue-600" />
            ) : (
              <Minus size={14} className="text-zinc-700" />
            )}
          </button>
          <span className="text-xs text-zinc-500">Smaller</span>
          <span className="text-zinc-300">|</span>
          <span className="text-xs text-zinc-500">Bigger</span>
          <button
            type="button"
            onClick={() => onPhotoZoom("photo", "enlarge")}
            disabled={zoomingField === "photo"}
            className="flex h-7 w-7 items-center justify-center rounded-sm border border-zinc-400 bg-white shadow-sm hover:bg-zinc-50 disabled:opacity-60"
            title="Enlarge product (crop tighter)"
            data-testid="product-image-zoom-in-button-photo"
          >
            {zoomingField === "photo" ? (
              <CircleNotch size={14} className="animate-spin text-blue-600" />
            ) : (
              <Plus size={14} className="text-zinc-700" />
            )}
          </button>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/bmp"
        className="hidden"
        onChange={onFileSelected}
        data-testid="product-image-file-input"
      />
    </div>
  );
}
